import { Injectable, inject, signal, computed } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';

export type PricingRegion = 'africa' | 'global';

const SESSION_KEY = 'kr_pricing_region';
const MANUAL_KEY  = 'kr_pricing_manual';

const AFRICA_CONTINENT = 'AF';

@Injectable({ providedIn: 'root' })
export class GeoRegionService {
  private readonly http = inject(HttpClient);

  /** Detected or manually chosen region. Defaults to 'africa' until resolved. */
  readonly region = signal<PricingRegion>('africa');

  /** True once the geo lookup has settled (or failed). */
  readonly resolved = signal<boolean>(false);

  /** True if the user manually overrode the detected region. */
  readonly isManual = signal<boolean>(false);

  readonly isAfrica = computed(() => this.region() === 'africa');

  constructor() {
    this._init();
  }

  private async _init(): Promise<void> {
    // Manual override stored from a previous visit — respect it immediately.
    const manual = sessionStorage.getItem(MANUAL_KEY);
    if (manual === 'africa' || manual === 'global') {
      this.region.set(manual);
      this.isManual.set(true);
      this.resolved.set(true);
      return;
    }

    // Auto-detected result cached for this session.
    const cached = sessionStorage.getItem(SESSION_KEY);
    if (cached === 'africa' || cached === 'global') {
      this.region.set(cached);
      this.resolved.set(true);
      return;
    }

    // Fresh lookup via ipapi.co (free tier — 1,000 req/day, no key needed).
    try {
      const data = await firstValueFrom(
        this.http.get<{ continent_code?: string }>('https://ipapi.co/json/')
      );
      const detected: PricingRegion =
        data?.continent_code === AFRICA_CONTINENT ? 'africa' : 'global';
      this.region.set(detected);
      sessionStorage.setItem(SESSION_KEY, detected);
    } catch {
      // On any failure keep the default 'africa' — avoids surprising a Nigerian
      // visitor with inflated global prices.
    } finally {
      this.resolved.set(true);
    }
  }

  /** Let the visitor manually switch region. Persists for the session. */
  setRegion(r: PricingRegion): void {
    this.region.set(r);
    this.isManual.set(true);
    sessionStorage.setItem(MANUAL_KEY, r);
    sessionStorage.setItem(SESSION_KEY, r);
  }
}
